import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, mergeProps as _mergeProps, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, vShow as _vShow, withModifiers as _withModifiers, withDirectives as _withDirectives, createElementBlock as _createElementBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "hotel-page-main-content" }
const _hoisted_2 = { class: "hotel-page-main-content__infos" }
const _hoisted_3 = { class: "hotel-page-main-content__infos-main" }
const _hoisted_4 = {
  ref: "linksContainer",
  class: "hotel-page-main-content__links"
}
const _hoisted_5 = {
  key: 0,
  class: "rpb_offers__fixed-button-wrapper rpb_container"
}
const _hoisted_6 = {
  key: 1,
  id: "hotel-page-main-content__section-hotels-nearby",
  class: "hotel-page-main-content__section-hotels-nearby"
}

import HotelInfos from '@lmt-rpb/HotelInfos/HotelInfos.vue';
import CustomerReviews from '@lmt-rpb/CustomerReviews/CustomerReviews.vue';
import QuickPillList from '@lmt-rpb/QuickPillList/QuickPillList.vue';
import { scrollToAnchorWithDynamicOffset, throttle } from '@utils/utils';
import { isClientSwitzerland } from '@utils/environmentUtils';
import { onMounted, onUnmounted, ref, computed, useTemplateRef } from 'vue';
import { ObserveVisibility as vObserveVisibility } from 'vue-observe-visibility';
import { OFFER_BOX_TELEPORT } from '@global-js/constants';
import type { OfferType } from '@/interfaces/hotel-list-types/hotel-list-types';
import type { OfferListTypoHotelData } from '@/interfaces/offer-list-types/offer-list-typo-hotel-data';
import type { StaticMaps } from '@/interfaces/google-maps';
import { useBoundaryObserver } from '@/js/composables/boundaryObserver';
import OfferList from './OfferList.vue';
import HotelsNearby from '../HotelsNearby/HotelsNearby.vue';
import { useSearchParams } from '@helper/composables/searchParams';
import { gaSelectContentAttr } from 'src/utils/analytics';
import SelectContentAnalytics from '@/js/modules/analytics/SelectContentAnalytics';
import InternalNavigationLink, {
	type InternalNavigationLinkProps,
} from '@lmt-rpb/Molecules/InternalNavigationBar/internals/InternalNavigationLink.vue';
import InternalNavigationBar from '@lmt-rpb/Molecules/InternalNavigationBar/InternalNavigationBar.vue';
import { storeToRefs } from 'pinia';
import { useBreakpointStore } from 'src/store/breakpointsStore';

type HotelPageNavLinkAnchors = 'description' | 'reviews' | 'offers';


export default /*@__PURE__*/_defineComponent({
  __name: 'HotelPageMainContent',
  props: {
    hotelInfo: {},
    mapData: {}
  },
  setup(__props: any) {

const props = __props;

const hotelInfosVisible = ref(false);
const offerListVisible = ref(false);
const customerReviewsVisible = ref(false);
const renderHotelsNearby = ref(false);
const showHotelsNearby = ref(true);
const showCustomerReviews = ref(true);
const showButton = ref(false);
const menu = useTemplateRef('navigationMenu');
const urlPathname = ref('');
const urlSearchParams = useSearchParams(true);
const bestOfferType = ref<OfferType | null>(null);
const offerContainer = ref<HTMLElement | null>(null);
const mainContentContainer = computed(() => menu.value?.$el);
const hrefWithHash = (hash: HotelPageNavLinkAnchors) => `${urlPathname.value}${urlSearchParams.value}#${hash}`;

const links = computed(() => {
	return {
		description: {
			anchorId: 'description',
			href: hrefWithHash('description'),
			label: 'Hotelbeschreibung',
		},
		reviews: {
			anchorId: 'reviews',
			href: hrefWithHash('reviews'),
			label: 'Bewertungen',
		},
		offers: {
			anchorId: 'offers',
			href: hrefWithHash('offers'),
			label: 'Angebote',
		},
	} satisfies Record<HotelPageNavLinkAnchors, InternalNavigationLinkProps>;
});

useBoundaryObserver(
	{ elementRef: mainContentContainer, shouldBeVisible: true },
	{ elementRef: offerContainer, shouldBeVisible: false, options: { threshold: 0 } }
);

const { isDesktop } = storeToRefs(useBreakpointStore());

const visibilityChanged = (isVisible: boolean) => {
	if (isVisible) renderHotelsNearby.value = true;
};

const handleScroll = throttle(() => {
	const scrollPosition = window.scrollY || document.documentElement.scrollTop;
	showButton.value = isDesktop.value ? scrollPosition >= 800 : scrollPosition >= 720; // Show button if scroll position is greater than or equal to 720/800
}, 150);

const pillClickAnalytics = (index: number) => {
	SelectContentAnalytics.selectContent({
		cId: 'hotelpage_hotels_nearby',
		cType: 'ux_plugin_quickpills',
		index: index + 1,
	});
};

onMounted(() => {
	urlPathname.value = window.location.pathname;
	window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
	window.removeEventListener('scroll', handleScroll);
});

const isCh = isClientSwitzerland();

const headerPropsQuickpill = {
	title: isCh ? 'Beliebte Ferienorte in der Nähe' : 'Beliebte Urlaubsorte in der Nähe',
	mobileTitle: isCh ? 'Ferienorte in der Nähe' : 'Urlaubsorte in der Nähe',
};

const searchParams = useSearchParams();
const quickPillList = computed(() =>
	props.hotelInfo.NearbyDestinations.map((el) => ({
		destination: el.Name,
		url: `${el.URL}?${searchParams.value}`,
		titleTag: el.Name,
	}))
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(InternalNavigationBar, {
        ref: "navigationMenu",
        class: "hotel-page-main-content__menu"
      }, {
        default: _withCtx(() => [
          (_ctx.hotelInfo?.Descriptions?.length || _ctx.hotelInfo?.Attributes?.length)
            ? (_openBlock(), _createBlock(InternalNavigationLink, _mergeProps({ key: 0 }, links.value.description, {
                "ga-attributes": _unref(gaSelectContentAttr)({ cId: 'menu_item_hotel_description' })
              }), null, 16 /* FULL_PROPS */, ["ga-attributes"]))
            : _createCommentVNode("v-if", true),
          (showCustomerReviews.value)
            ? (_openBlock(), _createBlock(InternalNavigationLink, _mergeProps({ key: 1 }, links.value.reviews, {
                "ga-attributes": _unref(gaSelectContentAttr)({ cId: 'menu_item_hotel_reviews' })
              }), null, 16 /* FULL_PROPS */, ["ga-attributes"]))
            : _createCommentVNode("v-if", true),
          _createVNode(InternalNavigationLink, _mergeProps(links.value.offers, {
            "ga-attributes": _unref(gaSelectContentAttr)({ cId: 'menu_item_hotel_offers' })
          }), null, 16 /* FULL_PROPS */, ["ga-attributes"])
        ]),
        _: 1 /* STABLE */
      }, 512 /* NEED_PATCH */),
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(HotelInfos, {
            id: "description",
            class: "hotel-page-main-content__description",
            "hotel-info": _ctx.hotelInfo,
            "best-offer-type": bestOfferType.value,
            "map-data": _ctx.mapData
          }, null, 8 /* PROPS */, ["hotel-info", "best-offer-type", "map-data"]),
          (showCustomerReviews.value)
            ? (_openBlock(), _createBlock(CustomerReviews, {
                key: 0,
                id: "reviews",
                "iff-code": _ctx.hotelInfo.Hotel.IffCode,
                "hotel-name": _ctx.hotelInfo.Hotel.Name,
                onNoReviews: _cache[0] || (_cache[0] = () => (showCustomerReviews.value = false))
              }, null, 8 /* PROPS */, ["iff-code", "hotel-name"]))
            : _createCommentVNode("v-if", true)
        ]),
        _createElementVNode("div", {
          class: _normalizeClass(_unref(OFFER_BOX_TELEPORT).HOTEL_DESCRIPTION)
        }, null, 2 /* CLASS */)
      ])
    ]),
    _createElementVNode("section", {
      id: "offers",
      ref_key: "offerContainer",
      ref: offerContainer,
      class: "hotel-page-main-content__offers"
    }, [
      _createVNode(OfferList, {
        "best-offer-type": bestOfferType.value,
        "onUpdate:bestOfferType": _cache[1] || (_cache[1] = ($event: any) => ((bestOfferType).value = $event))
      }, null, 8 /* PROPS */, ["best-offer-type"])
    ], 512 /* NEED_PATCH */),
    _createElementVNode("div", _hoisted_4, [
      ((hotelInfosVisible.value || customerReviewsVisible.value) && !offerListVisible.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _withDirectives(_createElementVNode("a", {
              class: "rpb_offers__fixed-button",
              href: "#offers",
              onClick: _cache[2] || (_cache[2] = _withModifiers(($event: any) => (_unref(scrollToAnchorWithDynamicOffset)($event)), ["prevent"]))
            }, " Preise & Termine ", 512 /* NEED_PATCH */), [
              [_vShow, showButton.value]
            ])
          ]))
        : _createCommentVNode("v-if", true),
      (showHotelsNearby.value)
        ? _withDirectives((_openBlock(), _createElementBlock("section", _hoisted_6, [
            (renderHotelsNearby.value)
              ? (_openBlock(), _createBlock(HotelsNearby, {
                  key: 0,
                  cyid: _ctx.hotelInfo.Location.City.TtCyid,
                  "city-name": _ctx.hotelInfo.Location.City.Name,
                  "hotel-name": _ctx.hotelInfo.Hotel.Name,
                  latitude: _ctx.hotelInfo.Location.Latitude,
                  longitude: _ctx.hotelInfo.Location.Longitude,
                  "search-distance": _ctx.hotelInfo.Location.SearchDistance,
                  "onHotelsNearby:HotelsFound": _cache[3] || (_cache[3] = (hotelsFound) => (showHotelsNearby.value = hotelsFound))
                }, null, 8 /* PROPS */, ["cyid", "city-name", "hotel-name", "latitude", "longitude", "search-distance"]))
              : _createCommentVNode("v-if", true)
          ])), [
            [_unref(vObserveVisibility), {
				callback: visibilityChanged,
				once: true,
				intersection: {
					root: null,
					rootMargin: '100px 0px 0px 0px',
				},
			}]
          ])
        : _createCommentVNode("v-if", true),
      _createVNode(QuickPillList, {
        class: "hotel-page-main-content__section-quick-pill-list",
        "header-props": headerPropsQuickpill,
        pills: quickPillList.value,
        onPillClick: pillClickAnalytics
      }, null, 8 /* PROPS */, ["pills"])
    ], 512 /* NEED_PATCH */)
  ], 64 /* STABLE_FRAGMENT */))
}
}

})